import React, { useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import { useTranslation } from "react-i18next";
import {
  getColor,
  teamLogo,
  teamBackground,
  getStandingsPositionStyle,
  getSeasonTeamFormat,
} from "../../../utils/Standings";
import { Link } from "react-router-dom";
import StandingsResult from "../../../components/Standings/StandingsResult";
import { i18n } from "../../../App";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";

const DriverResults = ({
  summary,
  maxRaces,
  driver,
  type,
}: {
  summary: DriverSummary[];
  maxRaces: number;
  driver: string;
  type: number;
}) => {
  const { t } = useTranslation();
  const [cols, setCols] = useState<{}[]>([]);
  const [rows, setRows] = useState<DriverResultsRow[]>([]);
  const [isExpanded, setIsExpanded] = useState(false);

  const setColsArray = () => {
    let cols_ = [];
    //Season - Game - Team
    cols_.push(
      {
        name: t("Championship"),
        selector: (row: DriverResultsRow) => row.season,
        sortFunction: (a: any, b: any) => a.season_id - b.season_id,
        width: "100px",
      },
      {
        name: t("Game"),
        selector: (row: DriverResultsRow) => {
          if (row.game == null) return <></>;
          return (
            <img
              src={`/assets/Games/${row.game}.png`}
              alt={row.game}
              title={row.game}
              className="game-icon"
            />
          );
        },
        width: "70px",
      },
      {
        name: t("Teams"),
        selector: (row: DriverResultsRow) => row.teams,
        width: "130px",
      },
    );
    for (let i = 0; i < maxRaces; i++) {
      cols_.push({
        name: i + 1,
        selector: (row: DriverResultsRow) => {
          let race_count = 0;
          let teams_array: any = row.teams_array;
          if (teams_array == null) {
            teams_array = [
              {
                name: "HillClimb",
                main_color: "#FFFFFF",
                id: null,
                logo: null,
              },
            ];
          }
          for (let result of row.results) {
            for (let race of result.Races) {
              if (race_count === i) {
                let JSX = [];
                for (let team_ of teams_array) {
                  const driver_ = race.Results.filter(
                    (driver_result) =>
                      driver_result.username === driver &&
                      (driver_result.team_name === team_.name ||
                        teams_array[0].name === "HillClimb"),
                  );
                  const driver_result_Q = result.hasOwnProperty("Qualy")
                    ? result.Qualy.filter((driver_) => driver_.name === driver)
                    : null;
                  let driver_found = null;
                  if (driver_[0] == null) {
                    for (let team__ of teams_array) {
                      const driver__ = race.Results.filter(
                        (driver_result) =>
                          driver_result.username === driver &&
                          driver_result.team_name === team__.name,
                      );
                      if (driver__[0] != null) driver_found = driver__;
                    }
                    if (driver_found != null)
                      JSX.push(
                        <div
                          key={`${i}a${result.id}`}
                          className={`standings-results driver-results-gp`}
                        ></div>,
                      );
                  }
                  if (driver_found == null) {
                    if (
                      driver_[0] != null ||
                      teams_array[0].name === team_.name
                    ) {
                      JSX.push(
                        <StandingsResult
                          key={`${i}b${result.id}`}
                          driver_result={driver_}
                          poleman={result.Poleman}
                          fastest={race.hot_lap_usernames}
                          most_leader={race.most_leader}
                          leaders={race.leaders}
                          qualy={driver_result_Q}
                          isDrivers={true}
                          gp={
                            <Link
                              to={`${t(
                                "routes:GrandPrix",
                              )}/${encodeURIComponent(
                                result.name.replace(/ /g, ""),
                              )}`}
                              className="driver"
                              title={result.name}
                            >
                              {result.initials}
                            </Link>
                          }
                        />,
                      );
                    } else
                      JSX.push(
                        <div
                          key={`${i}c${result.id}`}
                          className={`standings-results driver-results-gp`}
                        ></div>,
                      );
                  }
                }
                return <>{JSX}</>;
              }
              race_count++;
            }
          }

          return "";
        },
        width: "30px",
      });
    }
    // Points - Position
    cols_.push(
      {
        name: t("Points"),
        selector: (row: DriverResultsRow) => row.pointsJSX,
        width: "70px",
        sortFunction: (a: { points: number }, b: { points: number }) => {
          return a.points - b.points;
        },
      },
      {
        name: t("Position"),
        selector: (row: DriverResultsRow) => row.position,
        sortFunction: (
          a: { position_num: number | string },
          b: { position_num: number | string },
        ) => {
          if (
            typeof a.position_num === "number" &&
            typeof b.position_num === "number"
          )
            return a.position_num - b.position_num;
          else return 0;
        },
        width: "80px",
      },
    );
    setCols(cols_);
  };

  useEffect(() => {
    setColsArray();

    const rows_: DriverResultsRow[] = [];
    const total = isExpanded ? summary.length : Math.min(summary.length, 8);
    let sum = 0;
    summary.forEach((summary: DriverSummary, index: number) => {
      if (summary.type_races !== type) return;
      if (sum >= total) return;
      sum++;
      let teams_JSX = getSeasonTeamFormat(summary, index, t);
      if (summary.name !== "EVENTS")
        rows_.push({
          season: (
            <Link
              to={`${t("routes:Season")}/${encodeURIComponent(
                summary.name.replace(/ /g, ""),
              )}`}
              className="driver"
            >
              {summary.name}
            </Link>
          ),
          season_id: summary.championship_id,
          teams: teams_JSX,
          points: summary.points === null ? "-" : summary.points,
          pointsJSX: (
            <div
              className={`${
                typeof summary.position === "number" &&
                getStandingsPositionStyle(
                  summary.is_dsq ? "DSQ" : summary.position,
                )
              }`}
            >
              {summary.points}
            </div>
          ),
          position:
            summary.position === null ? (
              "-"
            ) : (
              <div
                className={`${
                  typeof summary.position === "number" &&
                  getStandingsPositionStyle(
                    summary.is_dsq ? "DSQ" : summary.position,
                  )
                }`}
              >
                {summary.position + "º"}
              </div>
            ),
          game: summary.game,
          position_num: summary.position === null ? "-" : summary.position,
          results: summary.results,
          teams_array: summary.teams_array,
        });
    });
    setRows(rows_);
  }, [i18n.language, isExpanded]);

  return (
    <div className="driver-results">
      <div
        className={`driver-results-table-container ${isExpanded ? "expanded" : ""}`}
      >
        <div className="inner-content">
          <DataTable columns={cols} data={rows} />
        </div>
        {summary.filter((s: any) => s.type_races === type)?.length > 8 && (
          <button
            className={`btn-ver-mas ${type === 0 ? "cir" : "hc"}`}
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded
              ? t("Show less").toUpperCase()
              : t("Show more").toUpperCase()}
            <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} />
          </button>
        )}
      </div>
    </div>
  );
};

export default DriverResults;
